import React, { useEffect, useState, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import { Col, Container, Row, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import {
  ABSpinner,
  useErrorService,
  useNotificationService,
  useUserService,
  Button,
  plans,
  sows,
} from '@abroad/components'
import ScheduledTab from '../scheduling/ScheduledTab'
import AvailabilityRequiredModal, {
  SCHEDULING_REQUIREMENT_VARIANT,
} from '../scheduling/AvailabilityRequiredModal'
import { TAB_TYPES } from '../scheduling/SettingTab'
import SessionSchedulingCalendar from '../scheduling/SessionSchedulingCalendar'
import ConditionalSessionSchedulingCalendar from '../scheduling/ConditionalSessionSchedulingCalendar'
import ConditionalRemainingSessionsSchedulingCalendar from '../scheduling/ConditionalRemainingSessionsSchedulingCalendar'
import { runSchedulingPreconditionChecks } from '../scheduling/schedulingPreconditions'
import SessionsTable from './SessionsTable'
import API from '../../utils/API'
import { messagesConstant } from '../../constants/messages'
import { sowStatus } from '@abroad/components/src/helper/common'

const ClientSessions = ({
  userPlan,
  isUserPlanExpired,
  isShowActiveSessions,
  hasTakenAssessment,
  clientTimezone = null,
}) => {
  const [sessions, setSessions] = useState([])
  const SUBTAB_TYPES = {
    ADD_VIEW: 'add_view',
    SCHEDULED: 'scheduled',
  }
  const [activeSubTab, setActiveSubTab] = useState(SUBTAB_TYPES.ADD_VIEW)
  const [scheduledEvents, setScheduledEvents] = useState([])
  const [isScheduledLoading, setIsScheduledLoading] = useState(false)
  const [scheduledPage, setScheduledPage] = useState(1)
  const [scheduledTotalPages, setScheduledTotalPages] = useState(1)
  const [scheduledHasMore, setScheduledHasMore] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [filterSessions, setFilterSessions] = useState(false)
  const [coachTimezone, setCoachTimezone] = useState(null)
  const [isLoadingTimezone, setIsLoadingTimezone] = useState(false)
  const [isAbroadCalendarConnected, setIsAbroadCalendarConnected] =
    useState(false)
  // Plans of this client that still have sessions to schedule (coach-scoped)
  const [pendingSchedulingRows, setPendingSchedulingRows] = useState([])
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(true)
  const [hasVideoCallLink, setHasVideoCallLink] = useState(true)
  // { row, isConditional, isFirstSession } while a calendar is open
  const [activeCalendar, setActiveCalendar] = useState(null)
  const [schedulingRequirementModal, setSchedulingRequirementModal] =
    useState(null)
  const { user } = useUserService()
  // Admins load every coach's bookings with this client; coaches load their own
  const isAllCoachesView = Boolean(user?.isAdmin)
  const [scheduledSort, setScheduledSort] = useState({
    sortBy: 'date',
    sortOrder: 'asc',
  })
  // Bumped on every page-1 fetch so responses to an older sort are dropped
  const scheduledRequestRef = useRef(0)
  const Error = useErrorService()
  const Notification = useNotificationService()
  const history = useHistory()
  const { clientId } = useParams()
  const location = useLocation()
  // const b2bGroupPlanName = sows.prefix.b2bGroup
  const normalGroupPlanName = 'Groups'

  const getAPIFunction = () => {
    if (isShowActiveSessions) {
      return API.CoachAPI.coachingSession.getSessionList(clientId)
    } else {
      return API.CoachAPI.coachingSession.getArchivedSessionList(clientId)
    }
  }

  useEffect(() => {
    const fetchSessionList = async () => {
      setIsLoading(true)
      try {
        const { data } = await getAPIFunction()
        if (data) {
          const individualSOWs = data?.indiSOWs
            ? data?.indiSOWs?.map((individualSOW) => ({
                ...individualSOW,
                planName: individualSOW?.planName,
                type: sows.planType.individual,
              }))
            : []
          const b2bSOWs = data?.b2bSOWs
            ? data?.b2bSOWs?.map((b2bSOW) => ({
                ...b2bSOW,
                planName: b2bSOW?.planName,
                type: sows.planType.b2b,
              }))
            : []

          const normalGroups = [
            {
              planName: normalGroupPlanName,
              sessions: data?.normalGroupSessions || [],
            },
          ]
          setSessions([...individualSOWs, ...b2bSOWs, ...normalGroups])
        }
      } catch (e) {
        Error.showError(e)
      }
      setIsLoading(false)
    }
    fetchSessionList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowActiveSessions, clientId])

  // Fetch timezone on component load
  useEffect(() => {
    const fetchCoachTimezone = async () => {
      setIsLoadingTimezone(true)
      try {
        const { data } = await API.CoachAPI.coachUser.getTimezone()
        const timezone = data?.timezone || null
        const isCalendarConnected = data?.isAbroadCalendarConnected || false
        setCoachTimezone(timezone)
        setIsAbroadCalendarConnected(isCalendarConnected)
      } catch (e) {
        Error.showError(e)
        setCoachTimezone(null)
        setIsAbroadCalendarConnected(false)
      } finally {
        setIsLoadingTimezone(false)
      }
    }
    fetchCoachTimezone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clicking the active column flips its direction; a new column starts ascending
  const handleScheduledSortChange = (sortBy) => {
    setScheduledSort((prev) => ({
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }))
  }

  const buildScheduledQuery = (page) => ({
    limit: 20,
    page,
    clientId,
    status: 'upcoming',
    sortBy: scheduledSort.sortBy,
    sortOrder: scheduledSort.sortOrder,
    ...(isAllCoachesView ? { allCoaches: true } : {}),
  })

  // Function to fetch scheduled events (can be called independently)
  const fetchScheduledEvents = useCallback(async () => {
    // Admins get every coach's bookings, so they need neither a coach timezone nor a calendar
    if (!isAllCoachesView) {
      // If timezone is not set, don't fetch scheduled events
      if (!coachTimezone) {
        setScheduledEvents([])
        setScheduledPage(1)
        setScheduledTotalPages(1)
        setScheduledHasMore(false)
        return
      }

      // Only fetch scheduled events if calendar is connected
      if (!isAbroadCalendarConnected) {
        setScheduledEvents([])
        setScheduledPage(1)
        setScheduledTotalPages(1)
        setScheduledHasMore(false)
        return
      }
    }

    const requestId = ++scheduledRequestRef.current
    setIsScheduledLoading(true)
    try {
      const { data: scheduledData } =
        await API.AdminAPI.scheduling.getCoachScheduledList(
          buildScheduledQuery(1),
        )
      // A newer sort or fetch started while this one was loading
      if (requestId !== scheduledRequestRef.current) return
      if (scheduledData?.success && scheduledData) {
        setScheduledEvents(scheduledData.events || [])
        const pagination = scheduledData.pagination || {}
        const apiPage = pagination.page || 1
        const apiTotalPages = pagination.totalPages || 1
        setScheduledPage(apiPage)
        setScheduledTotalPages(apiTotalPages)
        setScheduledHasMore(apiPage < apiTotalPages)
      } else {
        setScheduledEvents([])
        setScheduledPage(1)
        setScheduledTotalPages(1)
        setScheduledHasMore(false)
      }
    } catch (e) {
      if (requestId !== scheduledRequestRef.current) return
      Error.showError(e)
      setScheduledEvents([])
    } finally {
      if (requestId === scheduledRequestRef.current) {
        setIsScheduledLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    coachTimezone,
    isAbroadCalendarConnected,
    isAllCoachesView,
    scheduledSort,
  ])

  // Fetch scheduled events when scheduled tab is activated
  useEffect(() => {
    if (activeSubTab === SUBTAB_TYPES.SCHEDULED) {
      fetchScheduledEvents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, fetchScheduledEvents])

  useEffect(() => {
    // If navigated here with state.activateScheduled, activate scheduled subtab
    if (location?.state?.activateScheduled) {
      setActiveSubTab(SUBTAB_TYPES.SCHEDULED)
      // remove the state so it doesn't re-trigger
      history.replace(location.pathname, {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state])

  // Load this client's plans that still need scheduling, scoped to the logged-in coach.
  // Non-coach viewers get an error from the endpoint; that just means no button.
  const fetchPendingScheduling = useCallback(async () => {
    try {
      const { data } = await API.AdminAPI.scheduling.getCoachToBeSchedulingList(
        {
          page: 1,
          limit: 100,
          clientId,
        },
      )
      setPendingSchedulingRows(data?.success ? data?.data || [] : [])
      setIsScheduleEnabled(data?.isScheduleEnabled !== false)
      setHasVideoCallLink(data?.hasVideoCallLink !== false)
    } catch (e) {
      setPendingSchedulingRows([])
    }
  }, [clientId])

  // Refetch whenever Add/View is shown, so bookings cancelled or rescheduled
  // on the Scheduled tab are reflected here without a page refresh.
  useEffect(() => {
    if (activeSubTab === SUBTAB_TYPES.ADD_VIEW) {
      fetchPendingScheduling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, fetchPendingScheduling])

  const getPendingRow = (planId) =>
    pendingSchedulingRows.find((row) => String(row?.sowId) === String(planId))

  const handleScheduleSessionClick = (row) => {
    const canProceed = runSchedulingPreconditionChecks({
      hasVideoCallLink,
      isScheduleEnabled,
      onRequireVideoCallLink: () =>
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK,
        ),
      onRequireAvailability: () =>
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.AVAILABILITY,
        ),
    })
    if (!canProceed) return

    // Same dispatch as the Scheduling tab: first session vs. remaining sessions,
    // and the conditional flow for plans with up to 3 sessions.
    const totalSessions = row?.totalSessions || 0
    const isConditional =
      totalSessions <= 3 && row?.isConditionalSchedulingEnabled === true
    const isFirstSession = totalSessions === row?.remainingSessions
    setActiveCalendar({ row, isConditional, isFirstSession })
  }

  const renderScheduleSessionBtn = (planId, className) => {
    const row = getPendingRow(planId)
    const disabledReason = !coachTimezone
      ? 'Set your timezone to schedule sessions'
      : !row
      ? 'No sessions left to schedule for this sow plan'
      : ''
    const button = (
      <Button
        variant='gray'
        size='md'
        className={className}
        disabled={Boolean(disabledReason)}
        style={disabledReason ? { pointerEvents: 'none' } : undefined}
        onClick={() => {
          if (userPlan === plans.free.code) {
            Notification.showNotification(
              messagesConstant?.upgradeErrorMsg,
              'danger',
            )
          } else if (isUserPlanExpired) {
            Notification.showNotification(
              messagesConstant?.planExpiredErrorMsg,
              'danger',
            )
          } else {
            handleScheduleSessionClick(row)
          }
        }}>
        Schedule Session
      </Button>
    )
    if (!disabledReason) return button
    return (
      <OverlayTrigger
        placement='top'
        overlay={
          <Tooltip id={`schedule-disabled-${planId}`}>{disabledReason}</Tooltip>
        }>
        <span style={{ display: 'inline-block' }}>{button}</span>
      </OverlayTrigger>
    )
  }

  const closeCalendar = () => setActiveCalendar(null)

  const handleCalendarSuccess = async () => {
    setActiveCalendar(null)
    await fetchPendingScheduling()
    setActiveSubTab(SUBTAB_TYPES.SCHEDULED)
  }

  // No extra client fetch here; SchedulingTabFilters handles client loading when needed.

  const fetchScheduledMore = async () => {
    if (!scheduledHasMore) return
    // Pages loaded for an older sort are dropped, so they never mix into the list
    const requestId = scheduledRequestRef.current
    const nextPage = scheduledPage + 1
    setIsScheduledLoading(true)
    try {
      const { data } = await API.AdminAPI.scheduling.getCoachScheduledList(
        buildScheduledQuery(nextPage),
      )
      if (requestId !== scheduledRequestRef.current) return
      if (data?.success && data?.events && data.events.length > 0) {
        setScheduledEvents((prev) => [...prev, ...data.events])
        const pagination = data.pagination || {}
        const apiPage = pagination.page || nextPage
        const apiTotalPages = pagination.totalPages || scheduledTotalPages
        setScheduledPage(apiPage)
        setScheduledTotalPages(apiTotalPages)
        setScheduledHasMore(apiPage < apiTotalPages)
      } else {
        setScheduledHasMore(false)
      }
    } catch (e) {
      if (requestId === scheduledRequestRef.current) Error.showError(e)
    } finally {
      if (requestId === scheduledRequestRef.current) {
        setIsScheduledLoading(false)
      }
    }
  }

  useEffect(() => {
    if (filterSessions && sessionId) {
      const filteredData = sessions.map((session) => ({
        ...session,
        sessions: session.sessions.filter(
          (session) => session?._id !== sessionId,
        ),
      }))
      setSessions(filteredData)
      setSessionId(null)
      setFilterSessions(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSessions, sessionId])

  const renderAddSessionBtn = (
    status,
    remainingSession,
    billingIndefinite,
    totalSession,
    className,
  ) => {
    // Show Add Regular Session button when totalSession >= 1
    if (
      [
        sowStatus.IN_PROGRESS,
        sowStatus.TERMINATED,
        sowStatus.PAYMENT_FAILED,
      ].includes(status) &&
      (remainingSession > 0 || billingIndefinite === true)
    ) {
      return (
        <Button
          variant='gray'
          size='md'
          className={className}
          onClick={() => {
            if (userPlan === plans.free.code) {
              Notification.showNotification(
                messagesConstant?.upgradeErrorMsg,
                'danger',
              )
            } else if (isUserPlanExpired) {
              Notification.showNotification(
                messagesConstant?.planExpiredErrorMsg,
                'danger',
              )
            } else {
              history.push(`/coach/clients/${clientId}/session`)
            }
          }}>
          Add Regular Session
        </Button>
      )
    } else return null
  }

  const renderAddGrowthSessionBtn = (
    status,
    remainingSession,
    billingIndefinite,
    includeGrowthSession,
    totalSession,
    planId,
    className,
  ) => {
    // Show Add Growth Session button when totalSession >= 1 AND includeGrowthSession === true

    if (
      [
        sowStatus.IN_PROGRESS,
        sowStatus.TERMINATED,
        sowStatus.PAYMENT_FAILED,
      ].includes(status) &&
      (remainingSession > 0 || billingIndefinite === true) &&
      includeGrowthSession
    ) {
      return (
        <Button
          variant='gray'
          size='md'
          className={className}
          onClick={() => {
            if (userPlan === plans.free.code) {
              Notification.showNotification(
                messagesConstant?.upgradeErrorMsg,
                'danger',
              )
            } else if (isUserPlanExpired) {
              Notification.showNotification(
                messagesConstant?.planExpiredErrorMsg,
                'danger',
              )
            } else {
              history.push(
                `/admin/users/${clientId}/growth-plan/${planId}/create`,
              )
            }
          }}>
          Add Growth Session
        </Button>
      )
    } else return null
  }

  if (isLoading) {
    return <ABSpinner />
  }

  if (activeCalendar) {
    const { row, isConditional, isFirstSession } = activeCalendar
    let calendar
    if (isConditional && isFirstSession) {
      calendar = (
        <ConditionalSessionSchedulingCalendar
          coachDetails={row}
          onScheduleSuccess={handleCalendarSuccess}
          onBack={closeCalendar}
          timezone={coachTimezone}
        />
      )
    } else if (isConditional) {
      calendar = (
        <ConditionalRemainingSessionsSchedulingCalendar
          coachDetails={row}
          onScheduleSuccess={handleCalendarSuccess}
          onBack={closeCalendar}
          timezone={coachTimezone}
        />
      )
    } else {
      calendar = (
        <SessionSchedulingCalendar
          schedulingType='schedule-all'
          coachDetails={row}
          onScheduleSuccess={handleCalendarSuccess}
          onBack={closeCalendar}
          timezone={coachTimezone}
        />
      )
    }
    return <div className='pt-4'>{calendar}</div>
  }

  if (sessions?.length === 0 && !isLoading && !filterSessions) {
    return (
      <div className='d-flex flex-column'>
        <span className='s5 my-3'>No Sessions.</span>
      </div>
    )
  }
  const isScheduledTabDisabled =
    !isAllCoachesView && (!coachTimezone || isLoadingTimezone)

  // Get tooltip message based on why the tab is disabled
  const getScheduledTabTooltipMessage = () => {
    if (isLoadingTimezone) {
      return 'Loading timezone...'
    }
    if (!coachTimezone) {
      return 'Timezone must be set to view scheduled sessions'
    }
    return ''
  }

  const scheduledButton = (
    <Button
      variant='none'
      className='navTabs'
      disabled={isScheduledTabDisabled}
      style={{
        background: activeSubTab === SUBTAB_TYPES.SCHEDULED ? '#0A223C' : '',
        color: activeSubTab === SUBTAB_TYPES.SCHEDULED ? '#fff' : '',
        opacity: isScheduledTabDisabled ? 0.5 : 1,
        cursor: isScheduledTabDisabled ? 'not-allowed' : 'pointer',
      }}
      onClick={() => {
        if (!isScheduledTabDisabled) {
          setActiveSubTab(SUBTAB_TYPES.SCHEDULED)
        }
      }}>
      Scheduled
    </Button>
  )

  return (
    <section className='mt-30px client-sessions'>
      <div className='mb-4'>
        <Button
          variant='none'
          className='navTabs ml-0'
          style={{
            background: activeSubTab === SUBTAB_TYPES.ADD_VIEW ? '#0A223C' : '',
            color: activeSubTab === SUBTAB_TYPES.ADD_VIEW ? '#fff' : '',
          }}
          onClick={() => setActiveSubTab(SUBTAB_TYPES.ADD_VIEW)}>
          Add/View
        </Button>
        {isScheduledTabDisabled ? (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='scheduled-tab-tooltip'>
                {getScheduledTabTooltipMessage()}
              </Tooltip>
            }>
            <span style={{ display: 'inline-block' }}>{scheduledButton}</span>
          </OverlayTrigger>
        ) : (
          scheduledButton
        )}
      </div>

      {activeSubTab === SUBTAB_TYPES.ADD_VIEW && (
        <>
          {!isShowActiveSessions && !isLoading && (
            <h3 className='mb-4 s2'>Archived Sessions</h3>
          )}
          <Container fluid className='px-lg-0 pt-2'>
            <Row>
              <Col>
                {sessions.map((session, index) => {
                  return (
                    <React.Fragment key={index}>
                      <h3 className='s3 mb-4'>{session?.planName}</h3>
                      <div className='mb-2-5'>
                        <SessionsTable
                          data={session?.sessions?.sort(function (a, b) {
                            return new Date(a?.date) - new Date(b?.date)
                          })}
                          sessionId={sessionId}
                          setSessionId={setSessionId}
                          setFilterSessions={setFilterSessions}
                          type={session?.type}
                        />
                        {(session?.isCoachOfCurrentPlan ||
                          (session.sowStatus === sowStatus.TERMINATED &&
                            (session.remainingSession > 0 ||
                              (session.billingIndefinite === true &&
                                moment(session.terminatedOn).isAfter(
                                  moment(),
                                ))))) && (
                          <React.Fragment>
                            {session?.planName !== normalGroupPlanName && (
                              <React.Fragment>
                                <div className='d-flex gap-2 mt-2-5'>
                                  {renderAddSessionBtn(
                                    session.sowStatus,
                                    session?.remainingSession,
                                    session?.billingIndefinite,
                                    session?.totalSession,
                                    'line-height-normal border-radius-3',
                                  )}
                                  {renderAddGrowthSessionBtn(
                                    session.sowStatus,
                                    session?.remainingSession,
                                    session?.billingIndefinite,
                                    session?.includeGrowthSession,
                                    session?.totalSession,
                                    session?._id,
                                    'line-height-normal border-radius-3 ml-3',
                                  )}
                                  {renderScheduleSessionBtn(
                                    session?._id,
                                    'line-height-normal border-radius-3 ml-3',
                                  )}
                                </div>
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    </React.Fragment>
                  )
                })}
              </Col>
            </Row>
          </Container>
          <AvailabilityRequiredModal
            show={Boolean(schedulingRequirementModal)}
            variant={schedulingRequirementModal}
            handleClose={() => setSchedulingRequirementModal(null)}
            onConfirm={() => {
              const settingTab =
                schedulingRequirementModal ===
                SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK
                  ? TAB_TYPES.VIDEO_CALLING
                  : TAB_TYPES.AVAILABILITY
              setSchedulingRequirementModal(null)
              history.push('/coach/scheduling/setting', { settingTab })
            }}
          />
        </>
      )}

      {activeSubTab === SUBTAB_TYPES.SCHEDULED && (
        <ScheduledTab
          timezone={coachTimezone}
          isCalendarConnected={isAllCoachesView || isAbroadCalendarConnected}
          onFilterRender={() => {}}
          highlightDate={null}
          highlightEventIds={[]}
          isComeFromClientSessions={true}
          onHighlightClear={() => {}}
          prefetchedEvents={scheduledEvents}
          isPrefetchLoading={isScheduledLoading || isLoadingTimezone}
          prefetchHasMore={scheduledHasMore}
          onPrefetchLoadMore={fetchScheduledMore}
          skipClientFetch={true}
          showCoachColumn={isAllCoachesView}
          sort={scheduledSort}
          onSortChange={handleScheduledSortChange}
          onRescheduleSuccess={async () => {
            // Refetch timezone to get latest calendar connection status
            try {
              const { data } = await API.CoachAPI.coachUser.getTimezone()
              const timezone = data?.timezone || null
              const isCalendarConnected =
                data?.isAbroadCalendarConnected || false
              setCoachTimezone(timezone)
              setIsAbroadCalendarConnected(isCalendarConnected)
            } catch (e) {
              // ignore error
            }
            // Refetch scheduled events to get latest updated data
            await fetchScheduledEvents()
            // Navigate to sessions route and activate Scheduled tab
            history.push(`/coach/clients/${clientId}/sessions`, {
              activateScheduled: true,
            })
            setActiveSubTab(SUBTAB_TYPES.SCHEDULED)
          }}
        />
      )}
    </section>
  )
}

export default ClientSessions
